/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from 'react';
import axios from 'axios';
import baseUrl from '../BaseUrl';

// Send a push notification to a recipient list uploaded as a CSV, WITHOUT any
// database interaction. The CSV supplies the fcm_token(s) and any targeting
// columns (created_at, subscription_start, is_paid). Mirrors NotificationForm's
// look & feel (dashboard-box, form-control, #FE825B accent).
const ACCENT = '#FE825B';

const TARGET_MODES = [
  { value: 'all', label: 'All rows', needsDay: false },
  { value: 'signup_day', label: 'N days after signup (uses created_at)', needsDay: true },
  { value: 'purchase_day', label: 'N days after purchase (uses subscription_start)', needsDay: true },
  { value: 'paid', label: 'Paid only (uses is_paid)', needsDay: false },
  { value: 'free', label: 'Free only (uses is_paid)', needsDay: false },
];

const SendCsvNotificationForm = ({ token, onCancel }) => {
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [targetMode, setTargetMode] = useState('all');
  const [dayOffset, setDayOffset] = useState('');
  const [screenName, setScreenName] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [summary, setSummary] = useState(null);

  const needsDay = TARGET_MODES.find((m) => m.value === targetMode)?.needsDay;

  const validate = () => {
    const errs = {};
    if (!title.trim()) errs.title = 'Title is required';
    else if (title.length > 100) errs.title = 'Title must be 100 characters or fewer';
    if (!body.trim()) errs.body = 'Body is required';
    else if (body.length > 500) errs.body = 'Body must be 500 characters or fewer';
    if (!file) errs.file = 'A CSV file is required';
    else if (!/\.csv$/i.test(file.name)) errs.file = 'File must be a .csv';
    if (needsDay) {
      const n = Number(dayOffset);
      if (dayOffset === '' || !Number.isInteger(n) || n < 0 || n > 365) {
        errs.dayOffset = 'Enter a whole number of days between 0 and 365';
      }
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSummary(null);

    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('title', title.trim());
      formData.append('body', body.trim());
      formData.append('target_mode', targetMode);
      if (needsDay) formData.append('day_offset', dayOffset);
      if (screenName.trim()) formData.append('screen_name', screenName.trim());
      if (redirectUrl.trim()) formData.append('redirect_url', redirectUrl.trim());

      const response = await axios.post(`${baseUrl}/admin/api/send-csv-notification`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setSummary(response.data.data?.summary || null);
      } else {
        setErrorMsg(response.data.message || 'An error occurred.');
      }
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || 'Internal Server Error');
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = { fontWeight: 500, display: 'block', marginBottom: '6px' };
  const req = <span style={{ color: 'red' }}>*</span>;

  return (
    <div className="dashboard-box" style={{ marginBottom: '24px', padding: '24px' }}>
      <h3 style={{ marginBottom: '6px', fontWeight: 600 }}>Send to Uploaded List (CSV)</h3>
      <p style={{ color: '#6b7280', fontSize: '13px', marginBottom: '16px' }}>
        Upload a CSV to send a push notification straight from the file — no database lookup.
        The only required column is <code>fcm_token</code>; add <code>created_at</code>,{' '}
        <code>subscription_start</code>, or <code>is_paid</code> to enable the matching targets below.{' '}
        <a href="/sample-notification-recipients.csv" download style={{ color: ACCENT, fontWeight: 600 }}>
          Download sample CSV
        </a>
      </p>

      {errorMsg && (
        <div role="alert" style={{ background: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', borderRadius: '6px', padding: '10px 14px', marginBottom: '14px' }}>
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="row" style={{ marginBottom: '16px' }}>
          <div className="col-md-8">
            <label htmlFor="csv-notif-title" style={labelStyle}>
              Title {req}{' '}
              <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '13px' }}>({title.length}/100)</span>
            </label>
            <input
              id="csv-notif-title"
              type="text"
              className="form-control"
              value={title}
              maxLength={100}
              placeholder="Notification title"
              onChange={(e) => { setTitle(e.target.value); setErrors((p) => ({ ...p, title: undefined })); }}
            />
            {errors.title && <span style={{ color: 'red', fontSize: '13px' }}>{errors.title}</span>}
          </div>
        </div>

        <div className="row" style={{ marginBottom: '16px' }}>
          <div className="col-md-12">
            <label htmlFor="csv-notif-body" style={labelStyle}>
              Body {req}{' '}
              <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '13px' }}>({body.length}/500)</span>
            </label>
            <textarea
              id="csv-notif-body"
              className="form-control"
              value={body}
              maxLength={500}
              rows={3}
              placeholder="Notification body text"
              onChange={(e) => { setBody(e.target.value); setErrors((p) => ({ ...p, body: undefined })); }}
            />
            {errors.body && <span style={{ color: 'red', fontSize: '13px' }}>{errors.body}</span>}
          </div>
        </div>

        <div className="row" style={{ marginBottom: '16px' }}>
          <div className={needsDay ? 'col-md-8' : 'col-md-12'}>
            <label htmlFor="csv-notif-target" style={labelStyle}>Send to</label>
            <select
              id="csv-notif-target"
              className="form-control"
              value={targetMode}
              onChange={(e) => { setTargetMode(e.target.value); setErrors((p) => ({ ...p, dayOffset: undefined })); }}
            >
              {TARGET_MODES.map((m) => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>
          {needsDay && (
            <div className="col-md-4">
              <label htmlFor="csv-notif-day" style={labelStyle}>Days {req}</label>
              <input
                id="csv-notif-day"
                type="number"
                className="form-control"
                value={dayOffset}
                min={0}
                max={365}
                placeholder="e.g. 4"
                onChange={(e) => { setDayOffset(e.target.value); setErrors((p) => ({ ...p, dayOffset: undefined })); }}
              />
              {errors.dayOffset && <span style={{ color: 'red', fontSize: '13px' }}>{errors.dayOffset}</span>}
            </div>
          )}
        </div>

        <div className="row" style={{ marginBottom: '16px' }}>
          <div className="col-md-6">
            <label htmlFor="csv-notif-screen" style={labelStyle}>Screen name <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '13px' }}>(optional)</span></label>
            <input
              id="csv-notif-screen"
              type="text"
              className="form-control"
              value={screenName}
              placeholder="e.g. Dashboard"
              onChange={(e) => setScreenName(e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="csv-notif-redirect" style={labelStyle}>Redirect URL <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '13px' }}>(optional)</span></label>
            <input
              id="csv-notif-redirect"
              type="text"
              className="form-control"
              value={redirectUrl}
              placeholder="e.g. /dashboard"
              onChange={(e) => setRedirectUrl(e.target.value)}
            />
          </div>
        </div>

        <div className="row" style={{ marginBottom: '20px' }}>
          <div className="col-md-12">
            <label htmlFor="csv-notif-file" style={labelStyle}>Recipient CSV {req}</label>
            <input
              id="csv-notif-file"
              type="file"
              accept=".csv,text/csv"
              className="form-control"
              onChange={(e) => { setFile(e.target.files[0] || null); setErrors((p) => ({ ...p, file: undefined })); }}
            />
            {errors.file && <span style={{ color: 'red', fontSize: '13px' }}>{errors.file}</span>}
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
          <button
            type="submit"
            disabled={submitting}
            style={{ background: ACCENT, color: '#fff', border: `1px solid ${ACCENT}`, borderRadius: '5px', padding: '9px 14px', fontSize: '16px', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
          >
            {submitting ? 'Sending…' : 'Send Notification'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            style={{ background: '#fff', color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: '5px', padding: '9px 14px', fontSize: '16px', cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1 }}
          >
            Cancel
          </button>
        </div>
      </form>

      {summary && (
        <div role="status" style={{ marginTop: '20px', background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
          <h4 style={{ fontWeight: 600, marginBottom: '12px' }}>{summary.dry_run ? 'Dry Run — Preview (nothing was sent)' : 'Send Summary'}</h4>

          {summary.dry_run && (
            <div style={{ background: '#fef9c3', border: '1px solid #fde68a', color: '#854d0e', borderRadius: '6px', padding: '10px 14px', marginBottom: '12px', fontSize: '13px' }}>
              Sending is currently disabled. The users below would receive this notification once sending is enabled.
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <SummaryStat label="Rows" value={summary.total_rows} />
            <SummaryStat label={summary.dry_run ? 'Would receive' : 'Eligible'} value={summary.eligible} color="#065f46" bg="#d1fae5" border="#6ee7b7" />
            {!summary.dry_run && <SummaryStat label="Sent" value={summary.sent} color="#065f46" bg="#d1fae5" border="#6ee7b7" />}
            {!summary.dry_run && <SummaryStat label="Failed" value={summary.failed} color="#991b1b" bg="#fee2e2" border="#fca5a5" />}
            <SummaryStat label="Skipped" value={summary.skipped} />
            <SummaryStat label="Invalid rows" value={summary.invalid_rows} />
          </div>

          {Array.isArray(summary.recipients) && summary.recipients.length > 0 && (
            <div style={{ overflowX: 'auto', marginBottom: '10px' }}>
              <table className="table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ background: '#eef2f7', textAlign: 'left' }}>
                    <th style={{ padding: '8px 10px' }}>#</th>
                    <th style={{ padding: '8px 10px' }}>Email</th>
                    <th style={{ padding: '8px 10px' }}>FCM token</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.recipients.map((r, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '8px 10px', color: '#6b7280' }}>{i + 1}</td>
                      <td style={{ padding: '8px 10px' }}>{r.email || '—'}</td>
                      <td style={{ padding: '8px 10px', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '12px' }}>{r.fcm_token}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {summary.verify_query && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                Verify in DB (run this SQL to cross-check the recipients)
              </div>
              <pre style={{ background: '#1e293b', color: '#e2e8f0', padding: '10px 12px', borderRadius: '6px', fontSize: '12px', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all', margin: 0 }}>
                {summary.verify_query}
              </pre>
            </div>
          )}

          {Array.isArray(summary.failures) && summary.failures.length > 0 && (
            <DetailList title="Failures" items={summary.failures.map((f) => `${f.email || f.token_preview}: ${f.error}`)} />
          )}
          {Array.isArray(summary.skipped_details) && summary.skipped_details.length > 0 && (
            <DetailList title="Skipped (targeting)" items={summary.skipped_details.map((s) => `${s.email || '—'}: ${s.reason}`)} />
          )}
        </div>
      )}
    </div>
  );
};

const SummaryStat = ({ label, value, color = '#1e3a5f', bg = '#e0e7ef', border = '#c7d2e0' }) => (
  <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: '8px', padding: '8px 14px', minWidth: '84px', textAlign: 'center' }}>
    <div style={{ fontWeight: 700, fontSize: '18px', color }}>{value ?? 0}</div>
    <div style={{ fontSize: '12px', color: '#6b7280' }}>{label}</div>
  </div>
);

const DetailList = ({ title, items }) => (
  <details style={{ marginTop: '8px' }}>
    <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#374151' }}>
      {title} ({items.length})
    </summary>
    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '6px', wordBreak: 'break-all' }}>
      {items.join(' • ')}
    </div>
  </details>
);

export default SendCsvNotificationForm;
